import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import LanguageChoiceModal from './components/LanguageChoiceModal';
import Home from './pages/Home';
import About from './pages/About';
import Teams from './pages/Teams';
import Education from './pages/Education';
import Forum from './pages/Forum';
import ForumIntro from './pages/ForumIntro';
import Shopping from './pages/Shopping';
import Media from './pages/Media';
import Match from './pages/Match';
import Community from './pages/Community';
import Ride from './pages/Ride';
import AIEducation from './pages/AIEducation';
import Creators from './pages/Creators';
import PostDetail from './pages/PostDetail';
import CreatePost from './pages/CreatePost';
import Login from './pages/Login';
import HR from './pages/HR';
import HRProfile from './pages/HRProfile';
import AgentBI from './pages/AgentBI';
import SillyDragon from './pages/SillyDragon';
import Careers from './pages/Careers';
import Dashboard from './pages/Dashboard';
import Wiki from './pages/Wiki';
import Ldb from './pages/Ldb';
import ProtectedRoute from './components/ProtectedRoute';
import WikiAccessGate from './components/WikiAccessGate';
// Redirect old /info/* and /connections/* URLs to /forum/*
const RedirectInfoPost = () => {
  const { postId } = useParams();
  return <Navigate to={`/forum/post/${postId}`} replace />;
};
const RedirectInfoCategory = () => {
  const { categoryId } = useParams();
  return <Navigate to={`/forum/category/${categoryId}`} replace />;
};
const RedirectConnectionsPost = () => {
  const { postId } = useParams();
  return <Navigate to={`/forum/post/${postId}`} replace />;
};
const RedirectConnectionsCategory = () => {
  const { categoryId } = useParams();
  return <Navigate to={`/forum/category/${categoryId}`} replace />;
};

// Placeholder with translated name/description by path key
const PlaceholderByPath = ({ path }) => {
  const { t } = useLanguage();
  const name = t(`placeholder.${path}.name`);
  const rawDesc = t(`placeholder.${path}.description`);
  const description = rawDesc || (path === 'notFound' ? '' : t('placeholder.defaultDesc'));
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{name}</h2>
      {description ? <p className="text-gray-400 max-w-md text-sm md:text-base">{description}</p> : null}
    </div>
  );
};

// Wrapper component to conditionally apply Layout
const AppRoutes = () => {
  const location = useLocation();
  const isAIEducation = location.pathname === '/ai-education';
  const isWiki = location.pathname === '/wiki' || location.pathname.startsWith('/wiki/');
  const isLdb = location.pathname === '/ldb' || location.pathname.startsWith('/ldb/');

  if (isAIEducation) {
    return <AIEducation />;
  }

  if (isWiki) {
    return (
      <WikiAccessGate>
        <Routes>
          <Route path="/wiki/*" element={<Wiki />} />
        </Routes>
      </WikiAccessGate>
    );
  }

  if (isLdb) {
    return (
      <Routes>
        <Route
          path="/ldb/*"
          element={
            <ProtectedRoute roles={['admin', 'manager']}>
              <Ldb />
            </ProtectedRoute>
          }
        />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/ai" element={<Navigate to="/" replace />} />
        <Route path="/info" element={<ForumIntro />} />
        <Route path="/info/post/:postId" element={<RedirectInfoPost />} />
        <Route path="/info/create" element={<Navigate to="/forum/create" replace />} />
        <Route path="/info/category/:categoryId" element={<RedirectInfoCategory />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/post/:postId" element={<PostDetail />} />
        <Route path="/forum/create" element={<CreatePost />} />
        <Route path="/forum/category/:categoryId" element={<Forum />} />
        <Route path="/login" element={<Login />} />
        <Route path="/shopping" element={<Shopping />} />
        <Route path="/delivery" element={<PlaceholderByPath path="delivery" />} />
        <Route path="/education" element={<Education />} />
        <Route path="/media" element={<Media />} />
        <Route path="/ride" element={<Ride />} />
        <Route path="/streaming" element={<PlaceholderByPath path="streaming" />} />
        <Route path="/aid" element={<Community />} />
        <Route path="/community" element={<Navigate to="/aid" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/contact" element={<PlaceholderByPath path="contact" />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/join" element={<Navigate to="/careers" replace />} />
        <Route path="/hr" element={<HR />} />
        <Route path="/hr/profile" element={<HRProfile />} />
        <Route path="/creators" element={<Creators />} />
        <Route path="/agent-bi" element={<AgentBI />} />
        <Route path="/silly-dragon" element={<SillyDragon />} />
        {/* Backwards compatibility: /connections → /info, /ai → home */}
        <Route path="/connections" element={<Navigate to="/info" replace />} />
        <Route path="/connections/post/:postId" element={<RedirectConnectionsPost />} />
        <Route path="/connections/create" element={<Navigate to="/forum/create" replace />} />
        <Route path="/connections/category/:categoryId" element={<RedirectConnectionsCategory />} />
        <Route path="/offers" element={<PlaceholderByPath path="offers" />} />
        <Route path="/jobs" element={<PlaceholderByPath path="jobs" />} />
        <Route path="/visa" element={<PlaceholderByPath path="visa" />} />
        <Route path="/salary" element={<PlaceholderByPath path="salary" />} />
        <Route path="/match" element={<Match />} />
        <Route path="*" element={<PlaceholderByPath path="notFound" />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <LanguageProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <LanguageChoiceModal />
          <AppRoutes />
        </AuthProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
